import { JSX } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";

import Button from "@/components/buttons/Button.tsx";
import FilterListbox from "@/components/catalog/FilterListbox.tsx";
import FilterMobileMenu from "@/components/catalog/FilterMobileMenu.tsx";

import { useGetCatalogFilter, useGetCategory } from "@/hooks/api/content.hooks.ts";

const FilterSection = (): JSX.Element => {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const isFree = searchParams.get("is_free") == "true";

  const { id } = useParams() as { id: string };

  const { data: filters } = useGetCatalogFilter(parseInt(id));

  const removeFilters = () => setSearchParams(new URLSearchParams());

  const category = useGetCategory(Number.parseInt(id));

  const setIsFree = () => {
    if (isFree) {
      searchParams.delete("is_free");
    } else {
      searchParams.set("is_free", "true");
    }

    setSearchParams(searchParams);
  };

  return (
    <section className={"relative"}>
      <div
        className={"flex items-center justify-between pt-6 max-md:flex-col max-md:items-start max-md:gap-6 max-md:pt-5"}
      >
        <div className={"max-md:flex max-md:w-full max-md:items-center max-md:justify-between"}>
          <h1 className={"text-4xl font-semibold max-lg:text-3xl max-md:text-2xl"}>{category?.name}</h1>
          <FilterMobileMenu />
        </div>
      </div>
      <div className={"flex flex-wrap items-end gap-5 pt-[1.6875rem] max-sm:hidden"}>
        <FilterListbox
          title={t("General.audio-language")}
          values={[
            { id: "uzb", name: t("language-uz") },
            { id: "rus", name: t("language-ru") },
          ]}
          paramName={"language"}
        />
        {filters && filters.countries.length ? (
          <FilterListbox
            title={t("General.country")}
            isSearch={true}
            values={filters.countries}
            paramName={"country"}
          />
        ) : null}
        {filters && filters.genres.length ? (
          <FilterListbox
            title={t("General.genre", { count: 1 })}
            isSearch={true}
            values={filters.genres}
            paramName={"genre"}
          />
        ) : null}
        {filters && filters.years.length ? (
          <FilterListbox
            title={t("General.year")}
            isSearch={true}
            values={filters.years.map((year) => ({
              id: year,
              name: String(year),
            }))}
            paramName={"year"}
          />
        ) : null}
        <FilterListbox
          title={t("Actions.sort")}
          values={[
            { id: "rating", name: t("General.by-rating") },
            { id: "data_created", name: t("General.by-upload-date") },
          ]}
          paramName={"ordering"}
        />
        {filters && filters.distributors.length ? (
          <FilterListbox
            title={t("General.distributor", { count: 5 })}
            values={filters.distributors}
            paramName={"distributor"}
          />
        ) : null}

        <Button className="h-[3rem] w-min" size="medium" color={isFree ? "orange" : "white"} onClick={setIsFree}>
          {t("free")}
        </Button>
        <Button
          className="w-min text-nowrap bg-transparent p-3 text-primary hover:text-on-primary"
          onClick={removeFilters}
        >
          {t("Actions.reset-filters")}
        </Button>
      </div>
    </section>
  );
};

export default FilterSection;
